import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApplicationContext } from '@mysaiva/core/application'
import { DeveloperPlatformEvent, trackDeveloperPlatformEvent } from '../analytics/events'
import { useApiKeysClient } from './apiKeysClientContext'
import type { ApiKey, CreateApiKeyPayload, UpdateApiKeyPayload } from './apiKeys.types'

export const apiKeysQueryKey = (orgId: string) => ['developer-platform', 'api-keys', orgId] as const
export const apiKeyQueryKey = (orgId: string, id: string) =>
  ['developer-platform', 'api-keys', orgId, id] as const
export const apiKeyUsageQueryKey = (orgId: string, id: string) =>
  ['developer-platform', 'api-keys', orgId, id, 'usage'] as const

function useOrgId(): string {
  return useApplicationContext().orgContext.org.id
}

export function useApiKeys() {
  const client = useApiKeysClient()
  const orgId = useOrgId()
  return useQuery({
    queryKey: apiKeysQueryKey(orgId),
    queryFn: () => client.list()
  })
}

export function useApiKey(id: string | undefined) {
  const client = useApiKeysClient()
  const orgId = useOrgId()
  return useQuery({
    queryKey: apiKeyQueryKey(orgId, id ?? ''),
    queryFn: () => client.get(id ?? ''),
    enabled: Boolean(id)
  })
}

export function useApiKeyUsage(id: string | undefined) {
  const client = useApiKeysClient()
  const orgId = useOrgId()
  return useQuery({
    queryKey: apiKeyUsageQueryKey(orgId, id ?? ''),
    queryFn: () => client.getUsage(id ?? ''),
    enabled: Boolean(id),
    staleTime: 60_000
  })
}

function useInvalidate() {
  const queryClient = useQueryClient()
  const orgId = useOrgId()
  return (key?: ApiKey) => {
    void queryClient.invalidateQueries({ queryKey: apiKeysQueryKey(orgId) })
    if (key) queryClient.setQueryData(apiKeyQueryKey(orgId, key.id), key)
  }
}

export function useCreateApiKey() {
  const client = useApiKeysClient()
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (payload: CreateApiKeyPayload) => client.create(payload),
    onSuccess: ({ key }) => {
      invalidate(key)
      trackDeveloperPlatformEvent(DeveloperPlatformEvent.keyCreate, {
        key_id: key.id,
        environment: key.environment,
        scope_count: key.scopes.length
      })
    }
  })
}

export function useUpdateApiKey() {
  const client = useApiKeysClient()
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateApiKeyPayload }) =>
      client.update(id, payload),
    onSuccess: key => {
      invalidate(key)
      trackDeveloperPlatformEvent(DeveloperPlatformEvent.keyEdit, {
        key_id: key.id,
        environment: key.environment
      })
    }
  })
}

export function useRotateApiKey() {
  const client = useApiKeysClient()
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (id: string) => client.rotate(id),
    onSuccess: ({ key }) => {
      invalidate(key)
      trackDeveloperPlatformEvent(DeveloperPlatformEvent.keyRotate, {
        key_id: key.id,
        environment: key.environment
      })
    }
  })
}

export function useSetApiKeyEnabled() {
  const client = useApiKeysClient()
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      client.setEnabled(id, enabled),
    onSuccess: key => {
      invalidate(key)
      trackDeveloperPlatformEvent(
        key.status === 'active'
          ? DeveloperPlatformEvent.keyEnable
          : DeveloperPlatformEvent.keyDisable,
        { key_id: key.id, environment: key.environment }
      )
    }
  })
}

export function useDeleteApiKey() {
  const client = useApiKeysClient()
  const queryClient = useQueryClient()
  const orgId = useOrgId()
  return useMutation({
    mutationFn: (key: ApiKey) => client.remove(key.id).then(() => key),
    onSuccess: key => {
      queryClient.removeQueries({ queryKey: apiKeyQueryKey(orgId, key.id) })
      void queryClient.invalidateQueries({ queryKey: apiKeysQueryKey(orgId) })
      trackDeveloperPlatformEvent(DeveloperPlatformEvent.keyDelete, {
        key_id: key.id,
        environment: key.environment
      })
    }
  })
}
